import React from 'react';
import styled from 'styled-components';

import { AutoLinkField } from 'src/actions/integrations/integrationsResource';
import { Row, Section } from 'src/presentation/components/forms/Layout';
import { Checkbox } from 'src/presentation/components/agent_desktop_components';
import { ControlLabel, ControlDescription } from 'src/presentation/components/agent_desktop_components';
import Switch from 'src/presentation/components/forms/Switch';

import NameValueListEditor from 'src/presentation/components/pages/integrations/editorComponents/NameValueListEditor';
import { NameValueList } from 'src/presentation/components/pages/integrations/types';

export interface AutoLinkFieldProps {
  field: AutoLinkField;
  displayLabel: string;
  value: boolean;
}

export interface LookupSettingsEditorProps {
  autolinkFields: AutoLinkFieldProps[];
  dataAid?: string;
  maxQueryAttributeCount: number;
  onAutolinkFieldChange: (field: AutoLinkField, newState: boolean) => void;
  overwriteFlagAttribute: string;
  queryAttrListRef: React.MutableRefObject<NameValueList>;
  resultAttrListRef: React.MutableRefObject<NameValueList>;
  searchEnabled: boolean;
}

const LookupSettingsEditor = (props: LookupSettingsEditorProps): JSX.Element => {
  const {
    autolinkFields,
    dataAid = 'lookup-settings',
    maxQueryAttributeCount,
    overwriteFlagAttribute, // Path to the "overwritingEnabled" attribute within the integration config data object
    onAutolinkFieldChange,
    queryAttrListRef, // Working "ref" for the `searchQueryAttributes` list
    resultAttrListRef, // Working "ref" for the `searchResultAttributes` list
    searchEnabled,
  } = props;

  const checkboxRows = autolinkFields.map(fieldProps => (
    <ControlWrapperRow key={fieldProps.field}>
      <StyledCheckbox
        checked={fieldProps.value}
        data-aid={`autolink-${fieldProps.field}`}
        label={fieldProps.displayLabel}
        value=""
        onChange={() => {
          onAutolinkFieldChange(fieldProps.field, !fieldProps.value);
        }}
      />
    </ControlWrapperRow>
  ));

  const searchQueryAttributes = searchEnabled ? (
    <Section>
      <LabelWrapperRow>
        <ControlLabel>Search Query Attributes</ControlLabel>
      </LabelWrapperRow>
      <ControlWrapperRow>
        <ControlDescription>
          These attributes are sent when searching for customers. Attributes are case sensitive.
        </ControlDescription>
      </ControlWrapperRow>
      <Row>
        <NameValueListEditor
          addButtonLabel="Add an attribute"
          dataAid={`${dataAid}-query-field-editor`}
          items={queryAttrListRef.current}
          maxItemCount={maxQueryAttributeCount}
          maskedItemNames={[]}
          nameColumnLabel="Attribute"
          valueColumnLabel="Label"
          onChange={value => {
            queryAttrListRef.current = value;
          }}
        />
      </Row>
      <LabelWrapperRow>
        <ControlLabel>Search Results Attributes</ControlLabel>
      </LabelWrapperRow>
      <ControlWrapperRow>
        <ControlDescription>
          These attributes are displayed with search results. Attributes are case sensitive.
        </ControlDescription>
      </ControlWrapperRow>
      <Row>
        <NameValueListEditor
          addButtonLabel="Add an attribute"
          dataAid={`${dataAid}-results-field-editor`}
          items={resultAttrListRef.current}
          maxItemCount={maxQueryAttributeCount}
          maskedItemNames={[]}
          nameColumnLabel="Attribute"
          valueColumnLabel="Label"
          onChange={value => {
            resultAttrListRef.current = value;
          }}
        />
      </Row>
      <LabelWrapperRow>
        <ControlLabel>Auto-Linking Fields</ControlLabel>
      </LabelWrapperRow>
      <ControlWrapperRow>
        <ControlDescription>
          When an Agent lands on a Customer Profile in Gladly that isn&apos;t already linked to a profile in your
          external source, Gladly will look up the Customer using the following fields. If your external source responds
          with a unique profile with at least one auto-linking field match and no conflicts, Gladly will automatically
          link the profiles without any user intervention.
        </ControlDescription>
      </ControlWrapperRow>
      {checkboxRows}
    </Section>
  ) : null;

  return (
    <CustomStyleSection
      id="customer-linking-section"
      header="Customer Linking"
      headerMargin="small"
      data-aid="customer-linking-section"
    >
      {searchQueryAttributes}
      <Row>
        <Switch
          attr={overwriteFlagAttribute}
          label="Overwrite Existing Values"
          description={`Overwrite the existing name, address, and editable custom attributes in the Gladly 
                        customer profile with data from your external source`}
          useLocalControlState
        />
      </Row>
    </CustomStyleSection>
  );
};

const StyledCheckbox = styled(Checkbox)`
  white-space: nowrap;
`;

const ControlWrapperRow = styled(Row)`
  margin-bottom: 12px;
`;

const LabelWrapperRow = styled(Row)`
  margin-bottom: 0;
`;

const CustomStyleSection = styled(Section)`
  margin-bottom: 12px;
`;

export default LookupSettingsEditor;
